import { addDays, DateOnly } from '@if/core/date'
import { EFinancingProgram, EOriginSystemId, EPaymentPlan, EProvince } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import type { FormDraft } from '@if/core/yup'
import type { IfUser } from '@if/mui'
import {
  ActionButton,
  ActionLink,
  ArrowPopover,
  Banner,
  BodyText,
  ControlledCheckbox,
  ControlledNumberField,
  FieldsetCard,
  Icon,
  SplitContainer,
  useCan,
  useDebouncedValue,
  useFormContainerContext,
  useHoverPopover,
} from '@if/mui'
import { Card, CardContent, Divider, Grid, IconButton, Stack } from '@mui/material'
import { access } from '@src/access'
import { useSelectMerchantsDialog } from '@src/components/SelectMerchantsDialog'
import { useGetFirstPaymentDateOptions } from '@src/data/api/credit-api/credit-api'
import { useGetPersonalFunding } from '@src/data/api/worksheet-api/personal-loan-worksheet-api'
import { useGetPossibleTerms } from '@src/data/api/worksheet-api/worksheet-api'
import { getCoveragePerPayment, getPersonalFundingDto, isVariableInterest } from '@src/data/worksheet-selectors'
import { Constants, FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { EditPersonalLoanWorksheetDto, getMaxTermForAmount } from '@src/types/WorksheetSchema'
import { AlarmClock, Info, Plus, ShieldCheck } from 'lucide-react'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLoanTermSelection, useTermAndPlanSelection } from '../../ViewCreditApplicationPage/worksheet-hooks'
import PersonalLoanFundingSummary from './PersonalLoanFundingSummary'
import TermAndPlanSelect from './TermAndPlanSelect'
import WorksheetDetails from './worksheetDetails'

interface Props {
  merchant: Merchant
  minTerm: number
  maxTerm: number
  longTerm: number
  largeLoanAmount: number
  creditApplication: CreditApplication
  user: IfUser
  merchantPayments: { label: string; value: string; id: string }[]
  merchantsListFromParentId: Merchant[]
  merchantList: Merchant[]
  setMerchantList: Dispatch<SetStateAction<Merchant[]>>
  onAprTooHighChange: (value: boolean) => void
  onMaxPmtExceededChange: (value: boolean) => void
  onFundingReadyChange: (value: boolean) => void
  onValidTermAndPlanKeysChange: (keys: Set<string>) => void
}

const PersonalLoanWorksheetBody = ({
  merchant,
  minTerm,
  maxTerm,
  longTerm,
  largeLoanAmount,
  creditApplication,
  user,
  merchantPayments,
  merchantsListFromParentId,
  merchantList,
  setMerchantList,
  onAprTooHighChange,
  onMaxPmtExceededChange,
  onFundingReadyChange,
  onValidTermAndPlanKeysChange,
}: Props) => {
  // Draft-typed context: `watch`/`getValues` see the in-progress (nullable) shape,
  // so reads are honestly `T | null` with no `as` casts.
  const { methods } = useFormContainerContext<FormDraft<EditPersonalLoanWorksheetDto>>()
  const { control, setValue, trigger } = methods

  const deliveryOn = useWatch({ control, name: 'deliveryOn' })
  const amountRequested = useWatch({ control, name: 'amountRequested' }) ?? 0
  const debouncedAmountRequested = useDebouncedValue(amountRequested, Constants.AmountRequestedDebounceMs)
  const paymentFrequency = useWatch({ control, name: 'paymentFrequency' }) ?? 'monthly'
  const term = useWatch({ control, name: 'term' })
  const firstPaymentOn = useWatch({ control, name: 'firstPaymentOn' })
  const paymentPlanId = useWatch({ control, name: 'paymentPlanId' })
  const includeInsurance = useWatch({ control, name: 'includeInsurance' })
  const isHighEstimate = useWatch({ control, name: 'isHighEstimate' })
  const merchantPaymentsValues = useWatch({ control, name: 'merchantPayments' })
  const totalMerchantPayment = merchantPaymentsValues?.reduce((acc, curr) => Number(acc) + Number(curr.amount ?? 0), 0)
  const hasVariableInterest = isVariableInterest(paymentPlanId)
  const isBuydownEnabled = useCan(access.termAndPlanSelection)

  const { t } = useTranslation()

  // `interactive` so the pointer can reach the callout's content.
  const [infoTriggerProps, infoPopoverProps] = useHoverPopover({ interactive: true })
  const [highEstimateTriggerProps, highEstimatePopoverProps] = useHoverPopover({ interactive: true })

  const merchantsDialog = useSelectMerchantsDialog(merchantsListFromParentId, t('common.merchant'))

  const resetPayments = useCallback(() => {
    merchantList.forEach((_m, index) => {
      setValue(`merchantPayments.${index}.amount`, 0)
    })
  }, [merchantList, setValue])

  const onMerchantsSelected = useCallback(
    (newMerchants: Merchant[]) => {
      resetPayments()
      setMerchantList(newMerchants)
    },
    [resetPayments, setMerchantList],
  )

  const dto = useMemo(() => {
    if (deliveryOn && firstPaymentOn && debouncedAmountRequested && term) {
      return getPersonalFundingDto(
        creditApplication.id,
        debouncedAmountRequested,
        paymentFrequency,
        term,
        formatDate(deliveryOn),
        formatDate(firstPaymentOn),
        includeInsurance,
        creditApplication.applicant.currentAddress.stateIso,
        paymentPlanId,
        creditApplication.finalCreditDecision.interestRate,
        creditApplication.coApplicant !== null,
      )
    }
    return null
  }, [
    creditApplication,
    debouncedAmountRequested,
    deliveryOn,
    firstPaymentOn,
    includeInsurance,
    paymentFrequency,
    paymentPlanId,
    term,
  ])

  const computeMonthTermFrequency = useMemo(() => {
    if (!isBuydownEnabled && deliveryOn && firstPaymentOn && debouncedAmountRequested) {
      return {
        merchantId: creditApplication.merchantId,
        paymentPlanId,
        interestRate: creditApplication.finalCreditDecision.interestRate,
        hasCoapplicant: creditApplication.coApplicant !== null,
        stateIso: creditApplication.applicant.currentAddress.stateIso!,
        paymentFrequency,
        deliveryOn: formatDate(deliveryOn),
        firstPaymentOn: formatDate(firstPaymentOn),
        maxPmtAmount: creditApplication.finalCreditDecision.maxPmtAmount,
        amountRequested: debouncedAmountRequested,
        includeInsurance,
        include84Months: (creditApplication.finalCreditDecision.maxAmountFinanced ?? 0) >= largeLoanAmount,
      }
    }
    return null
  }, [
    isBuydownEnabled,
    creditApplication.merchantId,
    paymentPlanId,
    creditApplication.finalCreditDecision.interestRate,
    creditApplication.coApplicant,
    creditApplication.applicant.currentAddress.stateIso,
    paymentFrequency,
    deliveryOn,
    firstPaymentOn,
    creditApplication.finalCreditDecision.maxPmtAmount,
    creditApplication.finalCreditDecision.maxAmountFinanced,
    debouncedAmountRequested,
    includeInsurance,
    largeLoanAmount,
  ])

  const [possibleTerms, isLoadingTerms] = useGetPossibleTerms(
    computeMonthTermFrequency,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
  )

  const { loanTermChoicesList, handlePaymentPlanChange } = useLoanTermSelection({
    possibleTerms,
    creditApplication,
    merchant,
    paymentPlanId,
    term,
    regularMinTerm: minTerm,
    maxTermForAmount: getMaxTermForAmount(
      { ...FinancingProgramConfigs[EFinancingProgram.Personal], maxTerm, longTerm, largeLoanAmount },
      debouncedAmountRequested,
    ),
    setValue,
    isBuydownEnabled,
  })

  // Server-computed term & plan pairs — the buy-down counterpart of `useLoanTermSelection`.
  const {
    options: termAndPlanOptions,
    isLoading: isLoadingTermAndPlanOptions,
    selectedOption: selectedTermAndPlanOption,
  } = useTermAndPlanSelection({
    isBuydownEnabled,
    creditApplicationId: creditApplication.id,
    merchantId: creditApplication.merchantId,
    interestRate: creditApplication.finalCreditDecision.interestRate,
    hasCoapplicant: creditApplication.coApplicant !== null,
    stateIso: creditApplication.applicant.currentAddress.stateIso,
    paymentFrequency,
    deliveryOn: deliveryOn ? formatDate(deliveryOn) : null,
    firstPaymentOn: firstPaymentOn ? formatDate(firstPaymentOn) : null,
    maxPmtAmount: creditApplication.finalCreditDecision.maxPmtAmount,
    amountRequested: debouncedAmountRequested,
    includeInsurance: includeInsurance ?? false,
    paymentPlanId,
    term,
  })

  // The schema lives in the page container, so the accepted pairs are lifted there.
  useEffect(() => {
    onValidTermAndPlanKeysChange(
      isBuydownEnabled ? new Set(termAndPlanOptions.filter((o) => !o.disabled).map((o) => o.key)) : new Set(),
    )
  }, [isBuydownEnabled, termAndPlanOptions, onValidTermAndPlanKeysChange])

  // Surface a stale activation date on mount, before any option request.
  useEffect(() => {
    if (isBuydownEnabled) void trigger('deliveryOn')
  }, [isBuydownEnabled, trigger])

  const [computedPersonalLoanFunding, isComputing] = useGetPersonalFunding(
    creditApplication.id,
    dto,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
  )

  // Single source of truth: clear the funding when inputs are incomplete (dto null),
  // so the APR/maxPmt guards and the summary all reset together.
  const funding = dto ? computedPersonalLoanFunding : null

  const doesTermSupportInsurance = useMemo(() => {
    // With the server owning the options, legality is already reflected in what it returns.
    if (isBuydownEnabled) return true
    if (!possibleTerms?.monthlyTerms) return false
    const matchingTerm = possibleTerms.monthlyTerms.find((item) => item.term === Number(term))
    return matchingTerm ? matchingTerm.effectiveRateWithInsuranceIsLegal : false
  }, [isBuydownEnabled, possibleTerms, term])

  const isAPRTooHigh = useMemo(() => {
    const address = creditApplication.applicant.currentAddress
    if (!address || !funding) return false

    const { effectiveRate } = funding
    const { stateIso } = address

    const aprThresholds: Record<string, number> = {
      [EProvince.Newfoundland]: 27,
    }
    const threshold = aprThresholds[stateIso!]
    return threshold !== undefined && effectiveRate >= threshold
  }, [funding, creditApplication.applicant.currentAddress])

  // Surface the regulatory APR ceiling to the page so the pageActions submit can refuse it.
  useEffect(() => {
    onAprTooHighChange(isAPRTooHigh)
  }, [isAPRTooHigh, onAprTooHighChange])

  const firstPaymentDateOptions = useGetFirstPaymentDateOptions(
    creditApplication.id,
    creditApplication.financingProgramId,
    {
      deliveryOn: formatDate(deliveryOn!),
      systemOriginId: EOriginSystemId.MerchantDashboard,
      isHighEstimate: false,
      paymentFrequency: paymentFrequency,
    },
  )

  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (
      term === longTerm &&
      (creditApplication?.finalCreditDecision.maxAmountFinanced ?? 0) < largeLoanAmount &&
      creditApplication?.finalCreditDecision.maxTermDuration
    )
      setValue('term', creditApplication?.finalCreditDecision.maxTermDuration)
  }, [
    creditApplication?.finalCreditDecision.maxAmountFinanced,
    term,
    longTerm,
    largeLoanAmount,
    creditApplication?.finalCreditDecision.maxTermDuration,
    setValue,
  ])

  useEffect(() => {
    if (paymentPlanId !== EPaymentPlan.RegularDailyInterests) {
      setValue('isHighEstimate', false)
    }
  }, [paymentPlanId, setValue])

  useEffect(() => {
    // Re-syncs on merchantPaymentsValues too: a plain `methods.reset()` (Reset button) reverts
    // merchantPayments to the form's original defaultValues without changing amountRequested, so
    // the amountRequested-only dependency would miss it and leave the total-merchant-payment
    // validation stuck comparing against a stale amount (#69195).
    if (!user.isBannerUser && amountRequested && merchantPaymentsValues?.[0]?.amount !== amountRequested) {
      setValue(`merchantPayments.0.amount`, amountRequested)
    }
  }, [amountRequested, merchantPaymentsValues, setValue, user.isBannerUser])

  useEffect(() => {
    if (totalMerchantPayment !== undefined && merchantPaymentsValues?.[0]?.amount !== undefined) {
      void trigger('amountRequested')
    }
  }, [merchantPaymentsValues, totalMerchantPayment, trigger])

  useEffect(() => {
    const currentFirstPaymentDateAsString = formatDate(firstPaymentOn)
    const availableDatesAsString: (string | null)[] = []
    firstPaymentDateOptions.forEach((item) => {
      availableDatesAsString.push(formatDate(item))
    })

    if (availableDatesAsString.length > 0 && !availableDatesAsString.includes(currentFirstPaymentDateAsString)) {
      setValue('firstPaymentOn', new DateOnly(firstPaymentDateOptions[0]), {})
    }
  }, [setValue, firstPaymentOn, firstPaymentDateOptions])

  useEffect(() => {
    if (
      computeMonthTermFrequency?.maxPmtAmount &&
      funding &&
      funding.paymentForFrequency > computeMonthTermFrequency.maxPmtAmount
    ) {
      setError(new Error(t('common.errors.maxpaymentExceedsMaximumAllowed')))
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setError(null)
  }, [funding, computeMonthTermFrequency?.maxPmtAmount, t])

  // Surface the max-payment overrun to the page so the pageActions submit can refuse it.
  useEffect(() => {
    onMaxPmtExceededChange(error != null)
  }, [error, onMaxPmtExceededChange])

  // Surface funding readiness: block submission while a compute is in flight or no funding exists yet.
  useEffect(() => {
    onFundingReadyChange(!isComputing && funding != null)
  }, [isComputing, funding, onFundingReadyChange])

  const protectionPlanCost = getCoveragePerPayment(funding, term, paymentFrequency)

  const protectionPlanCheckbox = (
    <>
      <ControlledCheckbox
        name="includeInsurance"
        control={control}
        label={t('worksheet.addProtectionPlan')}
        icon={ShieldCheck}
        adornment={
          <IconButton {...infoTriggerProps} aria-label={t('worksheet.includeProtectionPlan')}>
            <Icon icon={Info} />
          </IconButton>
        }
        caption={
          protectionPlanCost > 0
            ? t('worksheet.protectionPlanCost', { amount: formatCurrency(protectionPlanCost) })
            : funding == null
              ? t('worksheet.protectionPlanCostPending')
              : t('worksheet.protectionPlanCostIfAdded')
        }
        disabled={!doesTermSupportInsurance}
      />
      <ArrowPopover {...infoPopoverProps}>
        <Stack spacing={2} sx={{ p: 2, maxWidth: 320 }}>
          <BodyText>{t('worksheet.protectionPlanInfo')}</BodyText>
          <ActionLink href="/protection-plan">{t('worksheet.learnMore')}</ActionLink>
        </Stack>
      </ArrowPopover>
    </>
  )

  return (
    <>
      {firstPaymentOn && isHighEstimate && (
        <Banner severity="warning" icon={AlarmClock} sx={{ mb: 2 }}>
          <BodyText sx={{ whiteSpace: 'pre-line' }}>
            {t('worksheet.highEstimateInvoiceWarning', {
              firstPaymentOn: formatDate(firstPaymentOn),
              invoiceDeadline: formatDate(addDays(new DateOnly(firstPaymentOn), -5)),
            })}
          </BodyText>
        </Banner>
      )}
      {error && (
        <Banner severity="error" title={t('common.error')} sx={{ mb: 2 }}>
          {error.message}
        </Banner>
      )}
      <SplitContainer
        main={
          <Stack spacing={3}>
            <FieldsetCard title={t('worksheet.loanDetails')}>
              <Stack spacing={2}>
                <WorksheetDetails
                  creditApplication={creditApplication}
                  merchantPayments={merchantPayments}
                  loanTermChoicesList={loanTermChoicesList}
                  isLoadingTerm={isLoadingTerms}
                  planPromoRules={merchant.planPromoRules}
                  firstPaymentDateOptions={firstPaymentDateOptions}
                  onPaymentPlanChange={handlePaymentPlanChange}
                  isBuydownEnabled={isBuydownEnabled}
                />
                {!isBuydownEnabled && protectionPlanCheckbox}
                {merchant?.canAcceptHighEstimate && (
                  <>
                    <ControlledCheckbox
                      name="isHighEstimate"
                      control={control}
                      label={t('worksheet.highEstimate')}
                      adornment={
                        <IconButton {...highEstimateTriggerProps} aria-label={t('worksheet.highEstimateTooltip')}>
                          <Icon icon={Info} />
                        </IconButton>
                      }
                      disabled={paymentPlanId !== EPaymentPlan.RegularDailyInterests}
                    />
                    <ArrowPopover {...highEstimatePopoverProps}>
                      <Stack sx={{ p: 2, maxWidth: 320 }}>
                        <BodyText>{t('worksheet.highEstimateTooltip')}</BodyText>
                      </Stack>
                    </ArrowPopover>
                  </>
                )}
              </Stack>
            </FieldsetCard>
            {isBuydownEnabled && (
              // The card and what shares it are the consumer's call: the term & plan
              // field, then the protection-plan opt-in that goes with the choice.
              <Card>
                <TermAndPlanSelect
                  options={termAndPlanOptions}
                  isLoading={isLoadingTermAndPlanOptions}
                  maxPmtAmount={creditApplication.finalCreditDecision.maxPmtAmount}
                />
                {/* Fences the opt-in off from the field above — it reads as a separate
                    decision, and with the table collapsed the two would otherwise merge. */}
                <Divider />
                <CardContent>{protectionPlanCheckbox}</CardContent>
              </Card>
            )}
            <FieldsetCard
              title={t('worksheet.paymentBreakdown')}
              action={
                user.isBannerUser ? (
                  <ActionButton
                    emphasis="normal"
                    startIcon={Plus}
                    onClick={async () => {
                      const result = await merchantsDialog.edit(merchantList)
                      if (result) onMerchantsSelected(result.merchants)
                    }}
                  >
                    {t('worksheet.addMerchantPayment')}
                  </ActionButton>
                ) : undefined
              }
            >
              <Stack spacing={2}>
                {merchantList?.map((m, index) => (
                  <Grid key={m.id} container spacing={1} alignItems="center">
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <BodyText>{m.name}</BodyText>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <ControlledNumberField
                        name={`merchantPayments.${index}.amount`}
                        control={control}
                        disabled={!user.isBannerUser}
                        label={t('worksheet.paymentAmount')}
                        isMoney
                      />
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            </FieldsetCard>
          </Stack>
        }
        aside={
          <PersonalLoanFundingSummary
            creditApplication={creditApplication}
            amountRequested={amountRequested}
            hasVariableInterest={hasVariableInterest}
            firstPaymentOn={formatDate(firstPaymentOn ?? null)}
            merchant={merchant}
            paymentPlanId={paymentPlanId}
            computedPersonalLoanFunding={funding}
            isComputing={isComputing}
            selectedTermAndPlanOption={selectedTermAndPlanOption}
          />
        }
      />
    </>
  )
}

export default PersonalLoanWorksheetBody
